import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { ArrowBack, Download, AccessTime, LocationOn } from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import QRCode from 'react-qr-code';
import { MainLayout } from '../components/layout/MainLayout';
import { db } from '../lib/firebase';
import { Route } from '../types/route';

export const RouteDetailPage: React.FC = () => {
  const { routeId } = useParams<{ routeId: string }>();
  const navigate = useNavigate();

  const [route, setRoute] = useState<Route | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 노선 정보 불러오기
  useEffect(() => {
    const fetchRoute = async () => {
      if (!routeId) return;

      try {
        const docRef = doc(db, 'routes', routeId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() } as Route;
          setRoute(data);
        } else {
          setError('노선 정보를 찾을 수 없습니다.');
        }
      } catch (err) {
        console.error('Error fetching route:', err);
        setError('노선 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchRoute();
  }, [routeId]);

  const handleBackToList = () => {
    navigate('/routes');
  };

  const handleDownloadQR = () => {
    if (!routeId) return;

    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;

    // SVG를 Canvas로 변환
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);

      // Canvas를 Blob으로 변환하여 다운로드
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `route_qr_${route?.name || routeId}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
      });
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (!route) {
    return (
      <MainLayout>
        <Box>
          <Alert severity="error">{error || '노선 정보를 찾을 수 없습니다.'}</Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToList}
            sx={{ mt: 2 }}
          >
            목록으로 돌아가기
          </Button>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box>
        {/* 뒤로가기 버튼 */}
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackToList}
          sx={{ mb: 3 }}
        >
          목록으로 돌아가기
        </Button>

        {/* 노선 기본 정보 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {route.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <AccessTime sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body1" color="text.secondary">
              출발 시간: {route.departureTime}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <LocationOn sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body1" color="text.secondary">
              정류장: {route.stops?.length || 0}개
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* QR 코드 섹션 */}
          <Paper sx={{ p: 3, flex: '0 0 auto' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              노선 QR 코드
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              이 QR 코드를 스캔하면 노선 정보를 확인할 수 있습니다.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                backgroundColor: '#fff',
                borderRadius: 1,
                border: '1px solid #e5e7eb',
              }}
            >
              <QRCode
                id="qr-code-svg"
                value={routeId || ''}
                size={200}
                level="H"
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleDownloadQR}
              fullWidth
              sx={{ mt: 2 }}
            >
              QR 코드 다운로드
            </Button>
          </Paper>

          {/* 정류장 목록 섹션 */}
          <Paper sx={{ p: 3, flex: '1 1 400px' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              정류장 목록
            </Typography>
            <Divider sx={{ my: 2 }} />
            {route.stops && route.stops.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600, width: '15%' }}>순서</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '55%' }}>정류장명</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '30%' }}>도착 시간</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {route.stops
                      .sort((a, b) => a.order - b.order)
                      .map((stop) => (
                        <TableRow key={stop.order}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} color="primary">
                              {stop.order}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">{stop.stationName}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">{stop.arrivalTime}</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  등록된 정류장이 없습니다.
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </MainLayout>
  );
};
